import type { DashboardContract, Locale } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Button } from "@chams-pay/design-system/components/button";
import { cn } from "@chams-pay/design-system/lib/utils";
import { BarChart3, Plus } from "lucide-react";
import { useRef } from "react";
import { arrearsStudentFilters, type StudentFilters } from "../../core/use-students.js";
import type { DashboardController } from "../../core/use-operations.js";
import {
  AcademicCollectionChart,
  AcademicYearSelector,
  ActivityCollectionsChart,
  CollectionGauge,
} from "../../ui/dashboard-charts.js";
import { Amount, Bidi, Count, UnavailableFigure } from "../../ui/figures.js";

type MessageKey = Parameters<typeof translate>[1];

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

function Panel({
  className,
  children,
}: {
  readonly className?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className={cn("rounded-lg border border-border bg-card shadow-e1", className)}>
      {children}
    </div>
  );
}

function collectionScopeLabel(scope: DashboardContract["collectionScope"], locale: Locale): string {
  if (scope.kind === "academic-year") return scope.academicYear;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn", {
    month: "long",
    year: "numeric",
  }).format(new Date(`${scope.month}-01T12:00:00Z`));
}

function DegradedKpi({ label, locale }: { readonly label: MessageKey; readonly locale: Locale }) {
  return (
    <Panel className="flex flex-col gap-2 p-4">
      <span className="chams-eyebrow">{translate(locale, label)}</span>
      <UnavailableFigure className="text-[26px] font-extrabold" />
      <span className="text-[11px] text-muted-2">{translate(locale, "dash.unavailable")}</span>
    </Panel>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-hidden="true">
      <div className="grid grid-cols-2 gap-3.5 min-[1280px]:grid-cols-[1.35fr_0.95fr_0.95fr_0.95fr]">
        <Panel className="flex items-center gap-[18px] p-[18px]">
          <div className="chams-skeleton size-[104px] shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="chams-skeleton h-2 w-3/5" />
            <div className="chams-skeleton h-3.5 w-4/5" />
            <div className="chams-skeleton h-2 w-2/5" />
          </div>
        </Panel>
        {[0, 1, 2].map((index) => (
          <Panel key={index} className="flex flex-col gap-2.5 p-[18px]">
            <div className="chams-skeleton h-2 w-1/2" />
            <div className="chams-skeleton h-[30px] w-4/5" />
            <div className="chams-skeleton mt-auto h-2 w-3/5" />
          </Panel>
        ))}
      </div>
      <div className="grid gap-3.5" style={{ gridTemplateColumns: "1.35fr 1fr" }}>
        <Panel className="flex h-[248px] items-end gap-3.5 p-[18px]">
          {[70, 62, 82, 50, 66, 44, 58].map((height, index) => (
            <div
              key={index}
              className="chams-skeleton flex-1"
              style={{ blockSize: `${height}%` }}
            />
          ))}
        </Panel>
        <Panel className="flex h-[248px] flex-col gap-3.5 p-[18px]">
          {[0, 1, 2].map((index) => (
            <div key={index} className="flex flex-col gap-1.5">
              <div className="chams-skeleton h-2 w-2/5" />
              <div className="chams-skeleton h-3 w-full" />
            </div>
          ))}
        </Panel>
      </div>
      <Panel className="grid grid-cols-3 gap-3.5 p-[18px]">
        {[0, 1, 2].map((index) => (
          <div key={index} className="flex flex-col gap-3">
            <div className="chams-skeleton h-2 w-1/3" />
            <div className="chams-skeleton h-[26px] w-1/4" />
            <div className="chams-skeleton h-2 w-full" />
          </div>
        ))}
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Offline surface (the shell stays; only the figures are unavailable)  */
/* ------------------------------------------------------------------ */

export function DesktopDashboardOffline({
  locale,
  lastSyncLabel,
  onTakePayment,
  onRetry,
}: {
  readonly locale: Locale;
  readonly lastSyncLabel: string;
  readonly onTakePayment: () => void;
  readonly onRetry: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  return (
    <section
      className="operations-screen flex flex-col gap-3.5"
      data-testid="dashboard-screen"
      aria-busy="false"
    >
      <div className="flex items-center gap-2.5 rounded-md border border-border bg-sunken px-3.5 py-3">
        <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-muted-foreground" />
        <span className="font-heading text-xs font-bold text-muted-foreground">
          {t("dash.offline")}
        </span>
        <span className="ms-auto text-[11px] text-muted-2">
          {t("dash.lastSync")} <Bidi>{lastSyncLabel}</Bidi>
        </span>
      </div>
      <Panel className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
        <span className="grid size-12 place-items-center rounded-full bg-sunken">
          <BarChart3 aria-hidden="true" className="size-5 text-muted-2" strokeWidth={1.6} />
        </span>
        <strong className="font-heading text-sm">{t("dash.offlineTitle")}</strong>
        <p className="max-w-[34ch] text-xs leading-relaxed text-muted-foreground">
          {t("dash.offlineBody")}
        </p>
        <div className="mt-1 flex gap-2.5">
          <Button size="sm" onClick={onTakePayment}>
            {t("dash.takePayment")}
          </Button>
          <Button size="sm" variant="outline" onClick={onRetry}>
            {t("dash.retry")}
          </Button>
        </div>
      </Panel>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Dashboard — desktop (5D)                                            */
/* ------------------------------------------------------------------ */

export function DesktopDashboardScreen({
  locale,
  controller,
  blocked,
  activityPeriodSelector,
  onOpenStudents,
  onOpenInstruments,
}: {
  readonly locale: Locale;
  readonly controller: DashboardController;
  readonly blocked: boolean;
  readonly activityPeriodSelector: React.ReactNode;
  readonly onOpenStudents: (filters?: StudentFilters) => void;
  readonly onOpenInstruments: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const { dashboard } = controller;
  const seen = useRef(false);
  if (dashboard) seen.current = true;

  if (!dashboard && !controller.failed && !seen.current) {
    return (
      <section className="operations-screen" data-testid="dashboard-screen" aria-busy="true">
        <DashboardSkeleton />
      </section>
    );
  }

  const failureNotice = controller.failed && (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-md border border-destructive-border bg-destructive-soft px-4 py-3"
    >
      <span
        aria-hidden="true"
        className="grid size-[18px] shrink-0 place-items-center rounded-full bg-destructive font-heading text-xs font-extrabold text-white"
      >
        !
      </span>
      <span className="flex flex-col gap-1">
        <strong className="font-heading text-xs text-destructive">{t("dash.failedTitle")}</strong>
        <span className="text-[11.5px] leading-relaxed text-destructive">
          {t(controller.failureBodyKey)}
        </span>
      </span>
      <Button
        variant="destructive"
        size="sm"
        className="ms-auto shrink-0"
        onClick={() => void controller.reload()}
      >
        {t("dash.retry")}
      </Button>
    </div>
  );

  if (!dashboard) {
    return (
      <section
        className="operations-screen flex flex-col gap-3.5"
        data-testid="dashboard-screen"
        aria-busy={controller.busy}
      >
        {failureNotice}
        <div className="grid grid-cols-2 gap-3.5 min-[1280px]:grid-cols-[1.35fr_0.95fr_0.95fr_0.95fr]">
          <DegradedKpi locale={locale} label="dash.collectionRate" />
          <DegradedKpi locale={locale} label="dash.netIncome" />
          <DegradedKpi locale={locale} label="dash.urgentArrears" />
          <DegradedKpi locale={locale} label="dash.pendingInstruments" />
        </div>
        <Panel className="grid h-[110px] place-items-center text-[11.5px] text-muted-2">
          {t("dash.unavailable")}
        </Panel>
      </section>
    );
  }

  const scopeLabel = collectionScopeLabel(dashboard.collectionScope, locale);
  const collectionTitle = t("dash.collectionRate");
  const gross = BigInt(dashboard.income.grossMad);
  const cancellations = BigInt(dashboard.income.cancellationsMad);
  const net = BigInt(dashboard.income.netMad);
  const arrearsBuckets = [
    {
      key: "one",
      filter: "one" as const,
      label: t("dash.oneMonth"),
      students: dashboard.arrears.oneMonthStudents,
      amountMad: dashboard.arrears.oneMonthMad,
      square: "bg-teal-300",
      bar: "bg-teal-300",
      track: "bg-sunken",
      cell: "",
      figure: "text-foreground",
    },
    {
      key: "two",
      filter: "two" as const,
      label: t("dash.twoMonths"),
      students: dashboard.arrears.twoMonthStudents,
      amountMad: dashboard.arrears.twoMonthMad,
      square: "bg-warning",
      bar: "bg-warning",
      track: "bg-sunken",
      cell: "",
      figure: "text-foreground",
    },
    {
      key: "three",
      filter: "three-plus" as const,
      label: t("dash.threePlus"),
      students: dashboard.arrears.threePlusMonthStudents,
      amountMad: dashboard.arrears.threePlusMonthMad,
      square: "bg-destructive",
      bar: "bg-destructive",
      track: "bg-[#f2e2e1]",
      cell: "bg-[#fdf9f9]",
      figure: "text-destructive",
    },
  ];
  const arrearsTotal = arrearsBuckets.reduce((total, bucket) => total + bucket.students, 0);
  const pendingCount = dashboard.pending.chequeCount + dashboard.pending.transferCount;

  return (
    <section
      className="operations-screen flex flex-col gap-3.5"
      data-testid="dashboard-screen"
      aria-busy={controller.busy}
    >
      {failureNotice}

      <div
        className={cn(
          "flex flex-col gap-3.5 transition-opacity duration-[140ms]",
          controller.busy ? "opacity-60" : "opacity-100",
        )}
      >
        {/* KPI row */}
        <div
          className={cn(
            "grid grid-cols-2 gap-3.5 min-[1280px]:grid-cols-[1.35fr_0.95fr_0.95fr_0.95fr]",
            blocked && "opacity-55",
          )}
        >
          <Panel className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(5.5rem,42%)] items-center gap-3 overflow-hidden p-[18px] [container-type:inline-size]">
            <div className="flex min-w-0 self-stretch flex-col gap-2.5">
              <span className="chams-eyebrow flex min-w-0 flex-wrap items-baseline gap-x-1 gap-y-0.5 leading-tight">
                <span>{collectionTitle}</span>
                <span className="text-[8px] font-semibold tracking-[0.06em] text-muted-foreground">
                  · <span className="capitalize">{scopeLabel}</span>
                </span>
              </span>
              <Amount
                value={dashboard.collection.settledAllocationsMad}
                locale={locale}
                className="text-[clamp(1.5rem,10cqi,1.875rem)] font-extrabold leading-none tracking-[-0.01em]"
              />
              <div className="mt-auto flex flex-col gap-1.5">
                <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
                  {t("dash.expected")}
                  <Amount
                    value={dashboard.collection.netObligationsMad}
                    locale={locale}
                    className="text-foreground"
                  />
                </span>
                <span aria-hidden="true" className="h-[21px]" />
              </div>
            </div>
            <div className="w-full max-w-32 place-self-center">
              <CollectionGauge
                percentage={dashboard.collection.percentage}
                label={`${collectionTitle} — ${scopeLabel}`}
                responsive
              />
            </div>
          </Panel>

          <Panel className="flex flex-col gap-2.5 p-[18px]">
            <span className="chams-eyebrow flex items-baseline gap-1 whitespace-nowrap">
              <span>{t("dash.netIncome")}</span>
              <span className="text-[8px] font-semibold tracking-[0.06em] text-muted-foreground">
                · {t("dash.monthToDate")}
              </span>
            </span>
            <Amount
              value={net}
              locale={locale}
              className="text-[30px] font-extrabold leading-none tracking-[-0.01em]"
            />
            <div className="mt-auto flex flex-col gap-1.5 text-[11px] text-muted-foreground">
              <span className="flex justify-between">
                {t("dash.gross")}
                <Amount value={gross} locale={locale} className="text-foreground" />
              </span>
              <span className="flex h-[21px] items-center justify-between">
                {t("dash.cancellations")}
                <Amount value={cancellations} locale={locale} signed tone="destructive" />
              </span>
            </div>
          </Panel>

          <button
            type="button"
            onClick={() => onOpenStudents(arrearsStudentFilters("overdue", dashboard.currentDate))}
            className="chams-focusable order-4 flex flex-col gap-2.5 rounded-lg border border-border bg-card p-[18px] text-start shadow-e1 transition-colors duration-fast hover:bg-surface-2"
          >
            <span className="chams-eyebrow flex items-center gap-1.5 text-warning">
              <span aria-hidden="true" className="size-1.5 rounded-full bg-warning" />
              {t("dash.overdueReceivables")}
            </span>
            <Amount
              value={dashboard.overdue.amountMad}
              locale={locale}
              className="text-[30px] font-extrabold leading-none"
            />
            <div className="mt-auto flex flex-col gap-1.5">
              <span className="text-[11px] text-muted-foreground">
                <Count value={dashboard.overdue.students} className="font-bold text-foreground" />{" "}
                {t("dash.studentsWord")}
              </span>
              <span aria-hidden="true" className="h-[21px]" />
            </div>
          </button>

          <button
            type="button"
            onClick={onOpenInstruments}
            className="chams-focusable order-3 flex flex-col gap-2.5 rounded-lg border border-border bg-card p-[18px] text-start shadow-e1 transition-colors duration-fast hover:bg-surface-2"
          >
            <span className="chams-eyebrow">{t("dash.pendingInstruments")}</span>
            <Count
              value={pendingCount}
              className={`block w-full text-[30px] font-extrabold leading-none text-foreground ${
                locale === "ar" ? "text-right" : "text-left"
              }`}
            />
            <div className="mt-auto flex flex-col gap-1.5">
              <span className="text-[11px] text-muted-foreground">
                {t("dash.pendingAmount")}{" "}
                <Amount
                  value={
                    BigInt(dashboard.pending.chequeMad) + BigInt(dashboard.pending.transferMad)
                  }
                  locale={locale}
                  className="font-bold text-foreground"
                />
              </span>
              <span className="flex gap-1.5">
                <span className="inline-flex h-[21px] items-center gap-1.5 rounded-full bg-warning-soft px-2 font-heading text-[10px] font-bold text-warning">
                  <Count value={dashboard.pending.chequeCount} /> {t("dash.cheques")}
                </span>
                <span className="inline-flex h-[21px] items-center gap-1.5 rounded-full bg-info-soft px-2 font-heading text-[10px] font-bold text-info">
                  <Count value={dashboard.pending.transferCount} /> {t("dash.transfers")}
                </span>
              </span>
            </div>
          </button>
        </div>

        {/* Activity and obligation-performance charts */}
        <div className="grid gap-3.5" style={{ gridTemplateColumns: "1.35fr 1fr" }}>
          <Panel className="p-[18px_20px_14px]">
            <div className="mb-4 flex items-center justify-between gap-4">
              <span className="font-heading text-sm font-bold">
                {t("dash.activityCollections")}
              </span>
              {activityPeriodSelector}
            </div>
            <ActivityCollectionsChart
              activity={dashboard.activity}
              period={dashboard.period}
              locale={locale}
              label={t("dash.activityCollections")}
            />
          </Panel>

          <Panel className="p-[18px_20px_14px]">
            <div className="mb-4 flex items-center justify-between gap-4">
              <span className="font-heading text-sm font-bold">{t("dash.academicCollection")}</span>
              <AcademicYearSelector
                collection={dashboard.academicCollection}
                locale={locale}
                onChange={controller.setAcademicYear}
              />
            </div>
            <AcademicCollectionChart
              collection={dashboard.academicCollection}
              locale={locale}
              label={t("dash.academicCollection")}
            />
          </Panel>
        </div>

        {/* Arrears band */}
        <Panel className="overflow-hidden">
          <div className="flex items-baseline gap-2.5 border-b border-sunken px-5 pb-3.5 pt-4">
            <span className="font-heading text-sm font-bold">{t("dash.arrearsByMonths")}</span>
          </div>
          <div className="grid grid-cols-3">
            {arrearsBuckets.map((bucket, index) => (
              <button
                key={bucket.key}
                type="button"
                onClick={() =>
                  onOpenStudents(arrearsStudentFilters(bucket.filter, dashboard.currentDate))
                }
                className={cn(
                  "chams-focusable flex flex-col gap-3 p-5 text-start transition-colors duration-fast hover:bg-sunken/60",
                  index < 2 && "border-e border-sunken",
                  bucket.cell,
                )}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className={cn("size-2 rounded-[2px]", bucket.square)} />
                  <span className="font-heading text-xs font-bold text-muted-foreground">
                    {bucket.label}
                  </span>
                </span>
                <span className="flex items-baseline gap-2">
                  <Count
                    value={bucket.students}
                    className={cn("text-[26px] font-extrabold leading-none", bucket.figure)}
                  />
                  <span className="text-[11px] text-muted-2">{t("dash.studentsWord")}</span>
                </span>
                <Amount
                  value={bucket.amountMad}
                  locale={locale}
                  className="text-[12px] font-bold text-muted-foreground"
                />
                <span className={cn("block h-2 overflow-hidden rounded-[4px]", bucket.track)}>
                  <span
                    className={cn("block h-full", bucket.bar)}
                    style={{
                      inlineSize: `${arrearsTotal > 0 ? Math.round((bucket.students / arrearsTotal) * 100) : 0}%`,
                    }}
                  />
                </span>
              </button>
            ))}
          </div>
        </Panel>

        {/* Current operational attention queue */}
        <Panel className="overflow-hidden">
          <div className="border-b border-sunken px-5 pb-3.5 pt-4">
            <span className="font-heading text-sm font-bold">{t("dash.attentionQueue")}</span>
          </div>
          <div className="grid grid-cols-2 divide-x divide-sunken rtl:divide-x-reverse">
            <button
              type="button"
              onClick={onOpenInstruments}
              className="chams-focusable flex min-h-24 flex-col gap-2 p-4 text-start hover:bg-sunken/60"
            >
              <span className="chams-eyebrow text-warning">{t("dash.pendingInstruments")}</span>
              <span className="flex items-baseline justify-between gap-2">
                <Count value={pendingCount} className="text-xl font-extrabold" />
                <Amount
                  value={
                    BigInt(dashboard.pending.chequeMad) + BigInt(dashboard.pending.transferMad)
                  }
                  locale={locale}
                  className="text-xs font-bold"
                />
              </span>
            </button>
            <button
              type="button"
              onClick={() =>
                onOpenStudents(
                  arrearsStudentFilters(
                    "registration",
                    dashboard.currentDate,
                    dashboard.currentAcademicYear,
                  ),
                )
              }
              className="chams-focusable flex min-h-24 flex-col gap-2 p-4 text-start hover:bg-sunken/60"
            >
              <span className="chams-eyebrow">{t("dash.registrationAttention")}</span>
              <span className="flex items-baseline justify-between gap-2">
                <Count
                  value={dashboard.overdueRegistration.students}
                  className="text-xl font-extrabold"
                />
                <Amount
                  value={dashboard.overdueRegistration.amountMad}
                  locale={locale}
                  className="text-xs font-bold"
                />
              </span>
            </button>
          </div>
        </Panel>

        {blocked && (
          <div className="flex items-center gap-2.5 rounded-md border border-dashed border-destructive-border bg-card px-3.5 py-3">
            <Button disabled>
              <Plus />
              {t("dash.takePayment")}
            </Button>
            <span className="text-[11.5px] leading-relaxed text-destructive">
              {t("dash.blockedHint")}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
